import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';
import { useRealtime } from '../../../shared/hooks/useRealtime';
import { debounce } from 'lodash';
import { 
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Trash2,
  Beaker,
  Dna,
  TestTube,
  Play,
  ArrowRight,
  Search
} from 'lucide-react';
import { getProject, deleteProject } from '../lib/projects';
import { 
  getOdysseyJobsForProject,
  getOdysseyGenerationCount,
  getLatestOdysseyJobForProject,
  hasOdysseyJobInProject
} from '../../jobs/lib/jobs';
import { listEntries, listDataEntries } from '../../notebook/lib/entries';
import type { Project } from '../../../shared/lib/supabase';
import { GenerationFlowchart } from '../../jobs/components/GenerationFlowchart';
// Type for Odyssey job from database
type OdysseyJob = {
  job_id: string;
  name: string | null;
  base_job_id: string | null;
  status: string;
  created_at: string | null;
  project_id: string | null;
  num_gens?: number | null;
};
import { toast } from 'sonner';

interface ProjectDetailsPageProps {
  onOpenChat?: () => void;
}

export function ProjectDetailsPage({ onOpenChat }: ProjectDetailsPageProps = {}) {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { openOdysseyTab } = useTabNavigation();
  const [project, setProject] = useState<Project | null>(null);
  const [odysseyJobs, setOdysseyJobs] = useState<OdysseyJob[]>([]);
  const [generationCount, setGenerationCount] = useState(0);
  const [hasOdysseyJob, setHasOdysseyJob] = useState(false);
  const [loading, setLoading] = useState(true);
  const [entriesCount, setEntriesCount] = useState(0);
  const [dataEntriesCount, setDataEntriesCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (projectId) {
      loadProjectDetails();
    }
  }, [projectId]);

  const loadProjectDetails = async () => {
    if (!projectId) return;

    try {
      // Load project details and entries in parallel
      const [projectData, entries, dataEntries] = await Promise.all([
        getProject(projectId),
        listEntries(projectId),
        listDataEntries(projectId)
      ]);

      setProject(projectData);
      setEntriesCount(entries.length);
      setDataEntriesCount(dataEntries.length);

      // Check if project has Odyssey jobs and load if exists
      const hasExistingOdysseyJob = await hasOdysseyJobInProject(projectId);
      setHasOdysseyJob(hasExistingOdysseyJob);

      if (hasExistingOdysseyJob) {
        const [jobs, count] = await Promise.all([
          getOdysseyJobsForProject(projectId),
          getOdysseyGenerationCount(projectId)
        ]);
        setOdysseyJobs(jobs);
        setGenerationCount(count);
      }
    } catch (error) {
      console.error('Error loading project details:', error);
      toast.error('Failed to load project details');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  // Debounced refresh for real-time updates
  const debouncedRefresh = useCallback(
    debounce(async () => {
      if (!projectId) return;
      
      try {
        // Only refresh odyssey jobs data, not the entire page
        const hasExistingOdysseyJob = await hasOdysseyJobInProject(projectId);
        setHasOdysseyJob(hasExistingOdysseyJob);

        if (hasExistingOdysseyJob) {
          const [jobs, count] = await Promise.all([
            getOdysseyJobsForProject(projectId),
            getOdysseyGenerationCount(projectId)
          ]);
          setOdysseyJobs(jobs);
          setGenerationCount(count);
        }
      } catch (error) {
        console.error('Error refreshing odyssey jobs:', error);
        // Don't show error toast for background updates
      }
    }, 1500), // 1.5 second debounce
    [projectId]
  );

  // Real-time subscription for odyssey jobs updates
  useRealtime({
    table: 'odyssey_jobs',
    projectId,
    onUpdate: (payload) => {
      console.log('🔄 Odyssey job updated:', payload);
      debouncedRefresh();
    },
    onInsert: (payload) => {
      console.log('➕ New odyssey job:', payload);
      debouncedRefresh();
    }
  });

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedRefresh.cancel();
    };
  }, [debouncedRefresh]);


  const handleDeleteProject = async () => {
    if (!project || !confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteProject(project.id);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'running':
      case 'queued':
        return <Clock className="h-5 w-5 text-blue-500 animate-pulse" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'running':
      case 'queued':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getWorkflowInfo = (type: string) => {
    switch (type) {
      case 'enzyme':
        return {
          icon: Beaker,
          color: 'blue',
          label: 'Enzyme Design'
        };
      case 'antibody':
        return {
          icon: Dna,
          color: 'teal',
          label: 'Antibody Design'
        };
      case 'peptide':
        return {
          icon: TestTube,
          color: 'amber',
          label: 'Peptide Design'
        };
      default:
        return {
          icon: FileText,
          color: 'gray',
          label: 'Unknown'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Project not found</p>
        <Link to="/projects" className="text-blue-600 hover:text-blue-700">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-6 py-8">
      {/* Header */}
      <div className="mb-10 animate-fade-in">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-light text-gray-900 mb-3">{project.name}</h1>
            {project.description && (
              <p className="text-lg text-gray-600 mb-3 max-w-3xl">{project.description}</p>
            )}
            <p className="text-sm text-gray-500 font-normal">
              Created {formatDate(project.created_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Project Overview Section */}
      <div className="bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden animate-fade-in mb-10" style={{animationDelay: '0.4s'}}>
        <div className="bg-gradient-to-r from-gray-100 to-gray-200 border-b border-gray-100">
          <div className="px-8 py-6 flex items-center justify-between">
            <h2 className="text-2xl font-normal text-gray-900">
              Project Overview
            </h2>
            {/* Stats moved to header */}
            <div className="flex items-center gap-6">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-normal text-gray-900">{generationCount}</span>
                <span className="text-sm text-gray-600">generations</span>
              </div>
              <div className="w-px h-6 bg-gray-300"></div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-normal text-gray-900">{entriesCount}</span>
                <span className="text-sm text-gray-600">entries</span>
              </div>
              <div className="w-px h-6 bg-gray-300"></div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-normal text-gray-900">{dataEntriesCount}</span>
                <span className="text-sm text-gray-600">tables</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {!hasOdysseyJob ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">To run the first generation, go to Protein Design on the lefthand sidebar.</p>
            </div>
          ) : odysseyJobs.length > 0 ? (
            <div className="space-y-6">
              {/* Generation Timeline */}
              <div className="bg-gray-50 rounded-sm p-4">
                <h3 className="text-xl font-normal text-gray-900 mb-3">Generation Timeline</h3>
                <GenerationFlowchart odysseyJobs={odysseyJobs} />
              </div>

              {/* Recent Generations Section */}
              <div className="bg-gray-50 rounded-sm p-4">
                <h3 className="text-xl font-normal text-gray-900 mb-3">Recent Generations</h3>
                
                {/* Search Bar */}
                <div className="relative mb-4">
                  <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search generations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-6 pr-3 py-2 text-sm bg-transparent border-0 border-b border-gray-300 focus:outline-none focus:border-gray-600"
                  />
                </div>
                
                <div className="space-y-4">
                  {(() => {
                    // Filter jobs based on search query
                    const filteredJobs = searchQuery 
                      ? odysseyJobs.filter(job => 
                          job.name?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                      : odysseyJobs.slice(0, 5); // Show only 5 most recent when not searching
                    
                    if (filteredJobs.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          {searchQuery ? 'No generations found matching your search' : 'No generations available'}
                        </div>
                      );
                    }
                    
                    return filteredJobs.map((job, index) => (
                      <div key={job.job_id} className="bg-gray-100 rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">
                        {job.name}
                      </h3>
                      {(job.status === 'completed' || job.status === 'processing' || job.status === 'pending') && (
                        <button
                          onClick={() => openOdysseyTab(job.job_id, job.name || 'Odyssey Job')}
                          className="bg-gradient-to-r from-gray-600 to-gray-800 text-white px-4 py-2 rounded-sm hover:from-gray-700 hover:to-gray-900 transition-all duration-300 font-medium shadow-lg text-sm"
                        >
                          {job.status === 'processing' || job.status === 'pending' ? 'View Current Job' : 'View Details'}
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Started: {formatDate(job.created_at)}</p>
                    </div>
                    
                    {/* Status, Last Run, and Workflow Info */}
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <div className="flex items-center gap-1 mt-1">
                          {getStatusIcon(job.status)}
                          <span className="text-sm font-medium capitalize">{job.status}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Last Run</p>
                        <p className="text-sm font-medium mt-1">{formatDate(job.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Workflow</p>
                        <div className="flex items-center gap-1 mt-1">
                          {(() => {
                            const Icon = getWorkflowInfo(project.workflow_type).icon;
                            return <Icon className="h-4 w-4" />;
                          })()}
                          <span className="text-sm font-medium capitalize">{project.workflow_type}</span>
                        </div>
                      </div>
                    </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

            </div>
          ) : null}
        </div>
      </div>

      </div>
    </div>
  );
}
